import { Coords } from 'google-map-react';
import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { ILocation } from '@gem/models/commonModel';
import { useTranslation } from '@gem/modules/i18n';
import { location, LocationAccess } from '@gem/modules/permissions';
import { ICommonPopupsProps } from '@gem/popups';
import { platformInfo } from '@gem/utils';

import Tasks from 'APP/Tasks';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Schedule } from 'STORE/Groups/Schedule';

export interface ISelectLocationParams {
  groupId?: string;
  onSelect(location: ILocation): void;
}

interface ISelectLocationPopup extends ILocation {
  isShowPopup: boolean;
  defaultZoom: number;
  defaultLatitude: number | null;
  defaultLongitude: number | null;
  map: any | null;
  maps: any | null;
  controlPosition: number | null;
  hasNoLocationAccessText: string;
  group: Group | null;
  isSheduledGroup: boolean;
  initialize(locationAccess: ILocation, zoom?: number): void;
  setCoordinates({ lat, lng }: Coords): void;
  setMaps({ map, maps }: { map: any; maps: any }): void;
  centerPin(): void;
  onSubmit(): void;
  onClose(): void;
}

const DEFAULT_LOCATION = {
  latitude: 0,
  longitude: 0,
};

const DEFAULT_ZOOM = 4;

export function useSelectLocationPopup({
  popupInstance,
  params,
}: ICommonPopupsProps<ISelectLocationParams>): ISelectLocationPopup {
  const { t } = useTranslation();
  const { groupId, onSelect } = params!;

  const presenter = useLocalStore<ISelectLocationPopup>(() => ({
    isShowPopup: false,

    defaultZoom: 18,
    defaultLatitude: null,
    defaultLongitude: null,

    latitude: null,
    longitude: null,

    map: null,
    maps: null,

    initialize(locationAccess: ILocation, zoom?: number): void {
      presenter.isShowPopup = true;
      presenter.defaultLatitude = locationAccess.latitude;
      presenter.defaultLongitude = locationAccess.longitude;
      presenter.latitude = locationAccess.latitude;
      presenter.longitude = locationAccess.longitude;

      if (zoom !== undefined) {
        presenter.defaultZoom = zoom;
      }
    },

    get group(): Group | null {
      return groupId ? Entities.GroupsStore.getGroupById(groupId) : null;
    },

    get isSheduledGroup(): boolean {
      return presenter.group instanceof Schedule;
    },

    get controlPosition(): number | null {
      return presenter.maps ? presenter.maps.ControlPosition.RIGHT_BOTTOM : null;
    },

    setCoordinates({ lat, lng }: Coords): void {
      presenter.latitude = lat;
      presenter.longitude = lng;
    },

    setMaps({ map, maps }: { map: any; maps: any }): void {
      presenter.map = map;
      presenter.maps = maps;
    },

    centerPin(): void {
      presenter.map.setZoom(presenter.defaultZoom);
      presenter.map.setCenter({ lat: presenter.latitude, lng: presenter.longitude });
    },

    get hasNoLocationAccessText(): string {
      if (platformInfo.isElectron && window.electronAPI?.isMac) {
        return t('system_preferences_location', {
          0: `<a href="x-apple.systempreferences:com.apple.preference.security?Privacy_LocationServices">${t(
            'system_preferences_common'
          )}</a>`,
          interpolation: { escapeValue: false },
        });
      }

      return t('gem4me_needs_location_access');
    },

    onSubmit(): void {
      onSelect({
        latitude: presenter.latitude,
        longitude: presenter.longitude,
      });
      presenter.onClose();
    },

    onClose(): void {
      popupInstance.close();
    },
  }));

  useEffect(() => {
    const initLocation = async (): Promise<void> => {
      const locationAccess = await location.getCurrentPosition();

      if (locationAccess.access === LocationAccess.Denied) {
        Tasks.app.showAlert({ html: presenter.hasNoLocationAccessText });
        presenter.onClose();
        return;
      }

      if (locationAccess.access === LocationAccess.Unavailable) {
        presenter.initialize(DEFAULT_LOCATION, DEFAULT_ZOOM);
        return;
      }

      presenter.initialize(locationAccess);
    };

    initLocation();
  }, []);

  return presenter;
}
